import * as React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { DateRangePicker, DollarFormatter, Page } from '@src/components';
import {
  fetchAdSummaryByAd,
  fetchAdSummaryBySubtheme,
  fetchAdSummaryByTheme,
  fetchAdSummaryFilterOptions,
  pauseAdSummaryAd,
} from '@src/api';
import api from '@src/api/clients';
import { formatDateToYMD } from '@src/utils/dateUtils';

import CreativeTable from './CreativeTable';
import AdPreviewDialog from './AdPreviewDialog';
import {
  VIEW_MODE_OPTIONS,
  CLIENT_FIELDS,
  MAX_CLIENT_META_PAGES,
  safeNumber,
  safeDiv,
  toOptionObjects,
  arraysEqual,
  computeDerivedMetrics,
  normalizeCampaignType,
  inferCreativeLabels,
  getAdAgeDays,
  formatAdAge,
  formatShortDate,
  formatPercentLabel,
  formatDeltaLabel,
  getLifecycleLabel,
  getKpiStage,
  getMaturityLabel,
  getPrimaryKpi,
  getFunnelBenchmarks,
  hasUsableBenchmarks,
  getDecisionCostMetrics,
  getBenchmarkDelta,
  getVerticalBenchmarkTargets,
  getCreativeScore,
  getBusinessScore,
  getOverallScore,
  getBenchmarkAdjustedScore,
  getConfidenceScore,
  getConfidenceLabel,
  getDirectionalDelta,
  getTrendLabel,
  getWhyDrivers,
  getTopWhyDriverChips,
  getWhyLabel,
  getCreativeVerdict,
  getDiagnosticLabel,
  getPausePriority,
  getBuildPriority,
  getPeerRankLabel,
  getStabilityLabel,
  getRecommendation,
  gateRecommendationByComConfidence,
  buildAdHierarchy,
  getDateRangeDays,
  getLastThirtyDays,
  getRecentStartDate,
  getRecommendedLookbackGuidance,
  buildPauseAuditNote,
  getDimensionColumns,
  buildPeerIndex,
  getPeersFromIndex,
  getSpendShareFromIndex,
  formatPriorityLabel,
} from './utils';

// ─── Client metadata loader ────────────────────────────────────────────────

async function fetchAllClientMetadata() {
  const allClients = [];
  let page = 1;
  while (page <= MAX_CLIENT_META_PAGES) {
    const response = await api.request({
      url: 'dashboard/clients/',
      method: 'GET',
      payload: { page, page_size: 250, ordering: 'name', fields: CLIENT_FIELDS },
    });
    const data = response?.data;
    if (Array.isArray(data)) { allClients.push(...data); break; }
    const results = Array.isArray(data?.results) ? data.results : [];
    allClients.push(...results);
    if (!data?.next) break;
    page += 1;
  }
  return allClients;
}

const getReportFetcher = (viewMode) => {
  if (viewMode === 'theme') return fetchAdSummaryByTheme;
  if (viewMode === 'subtheme') return fetchAdSummaryBySubtheme;
  return fetchAdSummaryByAd;
};

// ─── Main component ─────────────────────────────────────────────────────────

export default function ClientPerformanceAggregator() {
  const [defaultStart, defaultEnd] = React.useMemo(() => getLastThirtyDays(), []);
  const [startDate, setStartDate] = React.useState(defaultStart);
  const [endDate, setEndDate] = React.useState(defaultEnd);
  const [loading, setLoading] = React.useState(true);
  const [recentLoading, setRecentLoading] = React.useState(false);
  const [loadingClientMeta, setLoadingClientMeta] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [recentRows, setRecentRows] = React.useState([]);
  const [clientMetadata, setClientMetadata] = React.useState([]);
  const [selectedParentCompanies, setSelectedParentCompanies] = React.useState([]);
  const [selectedClients, setSelectedClients] = React.useState([]);
  const [viewMode, setViewMode] = React.useState('ad');
  const [selectedVerticals, setSelectedVerticals] = React.useState([]);
  const [selectedThemes, setSelectedThemes] = React.useState([]);
  const [selectedSubthemes, setSelectedSubthemes] = React.useState([]);
  const [selectedAdTypes, setSelectedAdTypes] = React.useState([]);
  const [selectedNotes, setSelectedNotes] = React.useState([]);
  const [showPausedAds, setShowPausedAds] = React.useState(false);
  const [backendCoverageFilter, setBackendCoverageFilter] = React.useState('all');
  const [previewRow, setPreviewRow] = React.useState(null);
  const [pauseNote, setPauseNote] = React.useState('');
  const [pauseLoading, setPauseLoading] = React.useState(false);
  const [expandedClients, setExpandedClients] = React.useState({});
  const [expandedCampaigns, setExpandedCampaigns] = React.useState({});
  const [expandedAdSets, setExpandedAdSets] = React.useState({});
  const [expandedQaRows, setExpandedQaRows] = React.useState({});
  const [qaRowDetails, setQaRowDetails] = React.useState({});
  const [qaRowLoading, setQaRowLoading] = React.useState({});
  const [qaRowErrors, setQaRowErrors] = React.useState({});
  const [showFilters, setShowFilters] = React.useState(false);
  const [filterOptions, setFilterOptions] = React.useState({
    verticals: [], themes: [], subthemes: [], adTypes: [], notes: [],
  });

  // ── Client metadata ─────────────────────────────────────────────────────

  React.useEffect(() => {
    let isMounted = true;
    const loadClients = async () => {
      setLoadingClientMeta(true);
      try {
        const clients = await fetchAllClientMetadata();
        if (!isMounted) return;
        setClientMetadata(clients.map((client = {}) => ({
          id: String(client.id),
          name: client.name || '',
          slug: client.slug || '',
          parentCompanyId: client.parent_company ? String(client.parent_company) : '__none__',
          parentCompanyName: client.parent_company_name || 'No Parent Company',
          hasMapping: Array.isArray(client.mapping_types) && client.mapping_types.length > 0,
        })));
      } catch (err) {
        console.error('Failed to load client metadata:', err);
        if (isMounted) { setClientMetadata([]); setError('Unable to load client metadata.'); }
      } finally {
        if (isMounted) setLoadingClientMeta(false);
      }
    };
    loadClients();
    return () => { isMounted = false; };
  }, []);

  const clientMetadataById = React.useMemo(() => {
    const next = new Map();
    clientMetadata.forEach((c) => next.set(c.id, c));
    return next;
  }, [clientMetadata]);

  const eligibleClients = React.useMemo(
    () => clientMetadata.filter((c) => !selectedParentCompanies.length || selectedParentCompanies.includes(c.parentCompanyId)),
    [clientMetadata, selectedParentCompanies]
  );

  const parentCompanyOptions = React.useMemo(() => {
    const deduped = new Map();
    eligibleClients.forEach((c) => {
      if (!deduped.has(c.parentCompanyId)) deduped.set(c.parentCompanyId, { id: c.parentCompanyId, name: c.parentCompanyName });
    });
    return Array.from(deduped.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [eligibleClients]);

  const clientOptions = React.useMemo(
    () => eligibleClients.map((c) => ({ id: c.id, name: c.name })).sort((a, b) => a.name.localeCompare(b.name)),
    [eligibleClients]
  );

  React.useEffect(() => {
    const allowed = new Set(parentCompanyOptions.map((o) => o.id));
    setSelectedParentCompanies((prev) => { const next = prev.filter((id) => allowed.has(id)); return arraysEqual(prev, next) ? prev : next; });
  }, [parentCompanyOptions]);

  React.useEffect(() => {
    const allowed = new Set(clientOptions.map((o) => o.id));
    setSelectedClients((prev) => { const next = prev.filter((id) => allowed.has(id)); return arraysEqual(prev, next) ? prev : next; });
  }, [clientOptions]);

  const effectiveClientIds = React.useMemo(() => {
    const scopedClients = selectedClients.length > 0
      ? eligibleClients.filter((c) => selectedClients.includes(c.id))
      : eligibleClients;
    return scopedClients.map((c) => c.id);
  }, [eligibleClients, selectedClients]);

  // ── Row mapper ──────────────────────────────────────────────────────────

  const mapSummaryRows = React.useCallback((summaryRows, referenceDate, currentViewMode) => (
    summaryRows
      .map((row = {}) => {
        const clientId = row.client_id ? String(row.client_id) : '';
        const clientMeta = clientId ? clientMetadataById.get(clientId) : null;
        const creativeLabels = inferCreativeLabels(row);
        const groupedHasBackendIntegration = safeNumber(row.backend_client_count) > 0;
        const hasBackendIntegration = Boolean(clientMeta?.hasMapping) || groupedHasBackendIntegration;

        const baseRow = {
          uuid: row.uuid || `${currentViewMode}-${row.theme || ''}-${row.subtheme || ''}-${row.ad_id || row.ad_name || row.client_id || ''}`,
          clientId, clientName: clientMeta?.name || row.client_name || row.client__name || '',
          parentCompanyId: clientMeta?.parentCompanyId || '__none__',
          parentCompanyName: clientMeta?.parentCompanyName || 'No Parent Company',
          hasMapping: hasBackendIntegration, hasBackendIntegration,
          verticalName: row.vertical || '',
          campaignType: normalizeCampaignType(row.campaign_type || ''),
          campaign: row.campaign || '', campaignId: row.campaign_id || '',
          themeName: row.theme || '', subthemeName: row.subtheme || '',
          adSetName: row.ad_set_name || '', adSetId: row.ad_set_id || '',
          adType: creativeLabels.adType,
          adName: row.ad_name || row.ad || '', adId: row.ad_id || '',
          notes: row.notes || '',
          isActive: row.active == null ? true : Boolean(row.active),
          activeStatusLabel: row.active == null || row.active ? 'Active' : 'Paused',
          facebookFormat: creativeLabels.facebookFormat,
          headline: row.headline || '', primaryText: row.primary_text || '',
          description: row.description || '', destinationUrl: row.destination_url || '',
          ctaType: row.cta_type || '',
          thumbnailUrl: row.thumbnail_url || '', imageUrl: row.image_url || '',
          videoId: row.video_id || '',
          clientCount: safeNumber(row.client_count),
          backendClientCount: safeNumber(row.backend_client_count),
          matureEndDate: row.mature_end_date || null,
          matureSpend: safeNumber(row.mature_spend_sum),
          matureLeads: safeNumber(row.mature_leads_sum),
          matureSelfBook: safeNumber(row.mature_self_books_sum),
          matureBooked: safeNumber(row.mature_booked_sum),
          matureScheduled: safeNumber(row.mature_scheduled_sum),
          matureIssues: safeNumber(row.mature_issues_sum),
          matureDemos: safeNumber(row.mature_demos_sum),
          matureGrossSold: safeNumber(row.mature_gross_sales_sum),
          matureNetSold: safeNumber(row.mature_net_sales_sum),
          matureNetAmount: safeNumber(row.mature_net_amount_sum),
          spend: safeNumber(row.total_spend_sum),
          impressions: safeNumber(row.impressions_sum),
          clicks: safeNumber(row.clicks_sum),
          landingPageViews: safeNumber(row.landing_page_views_sum),
          videoPlays3s: safeNumber(row.video_plays_3s_sum),
          thruplays: safeNumber(row.thruplays_sum),
          videoP25: safeNumber(row.video_p25_sum), videoP50: safeNumber(row.video_p50_sum),
          videoP75: safeNumber(row.video_p75_sum), videoP100: safeNumber(row.video_p100_sum),
          leads: safeNumber(row.total_leads_sum),
          selfBook: safeNumber(row.self_books_sum),
          booked: safeNumber(row.apps_booked_sum || row.booked_sum),
          scheduled: safeNumber(row.scheduled_sum),
          issues: safeNumber(row.issue_sum || row.issues_sum),
          demos: safeNumber(row.demo_sum || row.demos_sum),
          grossSold: safeNumber(row.gross_sold_sum || row.gross_sales_sum),
          netSold: safeNumber(row.net_sold_sum || row.net_sales_sum),
          netAmount: safeNumber(row.net_total_sum || row.net_amount_sum),
          saleCount: safeNumber(row.sale_count),
          avgDealValue: safeNumber(row.avg_deal_value),
          dealValueStddev: safeNumber(row.deal_value_stddev),
          dealValueCv: row.deal_value_cv == null ? null : safeNumber(row.deal_value_cv),
          dealValueStability: row.deal_value_stability || 'Unknown',
          dealValueStabilityDampener: safeNumber(row.deal_value_stability_dampener) || 0.8,
          adCreatedTime: row.ad_created_time || null,
        };

        const derived = computeDerivedMetrics(baseRow);
        const mergedRow = { ...baseRow, ...derived };
        const maturityLabel = getMaturityLabel(mergedRow);
        const adAgeDays = getAdAgeDays(baseRow.adCreatedTime, referenceDate);

        return {
          ...mergedRow,
          adAgeDays,
          adAgeLabel: formatAdAge(adAgeDays),
          maturityLabel,
          lifecycleLabel: getLifecycleLabel({
            ageDays: adAgeDays, spend: baseRow.spend, impressions: baseRow.impressions,
            leads: baseRow.leads, issues: baseRow.issues, demos: baseRow.demos, netSold: baseRow.netSold,
          }),
        };
      })
      .sort((left, right) => right.spend - left.spend)
  ), [clientMetadataById]);

  // ── Data fetching (Fix 6: main data loads first, recent deferred) ─────

  const buildPayload = React.useCallback(() => ({
    date_after: formatDateToYMD(startDate),
    date_before: formatDateToYMD(endDate),
    ordering: '-total_spend_sum',
    campaign_type: 'Broad',
    ...(selectedParentCompanies.length ? { parent_company_ids: selectedParentCompanies.join(',') } : {}),
    ...(effectiveClientIds.length ? { client_ids: effectiveClientIds.join(',') } : {}),
    ...(selectedVerticals.length ? { vertical: selectedVerticals.join(',') } : {}),
    ...(selectedThemes.length ? { theme: selectedThemes.join(',') } : {}),
    ...(selectedSubthemes.length ? { subtheme: selectedSubthemes.join(',') } : {}),
    ...(selectedAdTypes.length ? { ad_type: selectedAdTypes.join(',') } : {}),
    ...(selectedNotes.length ? { notes: selectedNotes.join(',') } : {}),
  }), [effectiveClientIds, endDate, selectedAdTypes, selectedNotes, selectedParentCompanies, selectedSubthemes, selectedThemes, selectedVerticals, startDate]);

  React.useEffect(() => {
    let isMounted = true;
    if (loadingClientMeta) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      setRecentRows([]);
      try {
        const summaryResp = await getReportFetcher(viewMode)(buildPayload());
        const summaryRows = Array.isArray(summaryResp?.data) ? summaryResp.data : Array.isArray(summaryResp?.data?.results) ? summaryResp.data.results : [];
        if (isMounted) setRows(mapSummaryRows(summaryRows, endDate, viewMode));
      } catch (err) {
        console.error('Failed to load ad performance aggregate:', err);
        if (isMounted) { setRows([]); setError('Unable to load aggregated ad performance data.'); }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, [buildPayload, endDate, loadingClientMeta, mapSummaryRows, viewMode]);

  // Fix 6: Deferred recent-window fetch — loads after main data is ready
  React.useEffect(() => {
    let isMounted = true;
    if (loading || !rows.length || loadingClientMeta) return;
    const loadRecent = async () => {
      setRecentLoading(true);
      try {
        const recentStart = getRecentStartDate(startDate, endDate);
        const recentPayload = { ...buildPayload(), date_after: formatDateToYMD(recentStart), date_before: formatDateToYMD(endDate) };
        const recentSummaryResp = await getReportFetcher(viewMode)(recentPayload);
        const recentSummaryRows = Array.isArray(recentSummaryResp?.data) ? recentSummaryResp.data : Array.isArray(recentSummaryResp?.data?.results) ? recentSummaryResp.data.results : [];
        if (isMounted) setRecentRows(mapSummaryRows(recentSummaryRows, endDate, viewMode));
      } catch (err) {
        console.error('Failed to load recent ad performance:', err);
        if (isMounted) setRecentRows([]);
      } finally {
        if (isMounted) setRecentLoading(false);
      }
    };
    loadRecent();
    return () => { isMounted = false; };
  }, [buildPayload, endDate, loading, loadingClientMeta, mapSummaryRows, rows.length, startDate, viewMode]);

  // ── Filter options ──────────────────────────────────────────────────────

  React.useEffect(() => {
    let isMounted = true;
    if (loadingClientMeta) return;
    const loadFilterOptions = async () => {
      try {
        const payload = {
          date_after: formatDateToYMD(startDate), date_before: formatDateToYMD(endDate),
          ...(selectedParentCompanies.length ? { parent_company_ids: selectedParentCompanies.join(',') } : {}),
          ...(effectiveClientIds.length ? { client_ids: effectiveClientIds.join(',') } : {}),
        };
        const response = await fetchAdSummaryFilterOptions(payload);
        const data = response?.data || {};
        if (!isMounted) return;
        setFilterOptions({
          verticals: toOptionObjects(data.verticals || []),
          themes: toOptionObjects(data.themes || []),
          subthemes: toOptionObjects(data.subthemes || []),
          adTypes: toOptionObjects(data.ad_types || []),
          notes: toOptionObjects(data.notes || []),
        });
      } catch (err) {
        console.error('Failed to load ad summary filter options:', err);
        if (!isMounted) return;
        setFilterOptions({ verticals: [], themes: [], subthemes: [], adTypes: [], notes: [] });
      }
    };
    loadFilterOptions();
    return () => { isMounted = false; };
  }, [effectiveClientIds, endDate, loadingClientMeta, selectedParentCompanies, startDate]);

  // ── Filtered rows ─────────────────────────────────────────────────────

  const filteredRows = React.useMemo(
    () => rows.filter((row) => (
      (backendCoverageFilter !== 'backend_only' || row.hasBackendIntegration)
      && (viewMode !== 'ad' || showPausedAds || row.isActive)
    )),
    [backendCoverageFilter, rows, showPausedAds, viewMode]
  );

  const filteredRecentRows = React.useMemo(
    () => recentRows.filter((row) => (backendCoverageFilter !== 'backend_only' || row.hasBackendIntegration)),
    [backendCoverageFilter, recentRows]
  );

  // ── Decision engine (Fix 1: pre-indexed peers for O(1) lookups) ───────

  const decisionRows = React.useMemo(() => {
    if (viewMode !== 'ad') {
      return filteredRows.map((row) => {
        const confidenceScore = getConfidenceScore(row);
        return {
          ...row, confidenceScore,
          confidenceLabel: getConfidenceLabel(confidenceScore),
          spendShareLabel: '-', peerRankLabel: '-', stabilityLabel: '-', trendLabel: '-',
          whyLabel: 'Grouped summary row.',
        };
      });
    }

    // Fix 1: Build peer indexes once — O(n) — instead of filtering per row — O(n²)
    const peerIndex = buildPeerIndex(filteredRows);
    const recentPeerIndex = buildPeerIndex(filteredRecentRows);
    const recentRowsByUuid = new Map(filteredRecentRows.map((row) => [row.uuid, row]));

    return filteredRows.map((row) => {
      const primaryKpi = getPrimaryKpi(row);
      const recentRow = recentRowsByUuid.get(row.uuid) || row;
      const recentPrimaryKpi = getPrimaryKpi(recentRow);

      // O(1) peer lookups via index
      const clientVerticalPeers = getPeersFromIndex(row, peerIndex, 'client');
      const agencyVerticalPeers = getPeersFromIndex(row, peerIndex, 'agency');
      const backendClientVerticalPeers = clientVerticalPeers.filter((p) => p.hasBackendIntegration);
      const backendAgencyVerticalPeers = agencyVerticalPeers.filter((p) => p.hasBackendIntegration);

      // Fix 8: Vertical-aware benchmark targets
      const verticalTargets = getVerticalBenchmarkTargets(agencyVerticalPeers);
      const clientBenchmarks = getFunnelBenchmarks(backendClientVerticalPeers);
      const agencyBenchmarks = getFunnelBenchmarks(backendAgencyVerticalPeers);
      const selectedBenchmarks = hasUsableBenchmarks(clientBenchmarks) ? clientBenchmarks : agencyBenchmarks;

      const decisionCostMetrics = getDecisionCostMetrics(row, selectedBenchmarks);
      const { predictedCostOfMarketing, effectiveCostOfMarketing, comConfidenceBand, comConfidenceLabel, dealValueStability, dealValueStabilityDampener, decisionCostActualWeight, decisionCostLabel } = decisionCostMetrics;

      const decisionCostKpi = effectiveCostOfMarketing > 0
        ? { key: 'effectiveCostOfMarketing', label: decisionCostLabel, better: 'lower', benchmarkScope: 'client_vertical', isEligible: (c) => safeNumber(c.effectiveCostOfMarketing) > 0 }
        : primaryKpi;

      const enrichedClientVerticalPeers = clientVerticalPeers.map((p) => ({ ...p, ...getDecisionCostMetrics(p, selectedBenchmarks) }));
      const enrichedAgencyVerticalPeers = agencyVerticalPeers.map((p) => ({ ...p, ...getDecisionCostMetrics(p, selectedBenchmarks) }));

      const enrichedRow = { ...row, effectiveCostOfMarketing };
      const clientVerticalDelta = getBenchmarkDelta(enrichedRow, enrichedClientVerticalPeers, decisionCostKpi);
      const agencyVerticalDelta = getBenchmarkDelta(enrichedRow, enrichedAgencyVerticalPeers, decisionCostKpi);
      const benchmarkDelta = decisionCostKpi?.benchmarkScope === 'agency_vertical' ? agencyVerticalDelta : clientVerticalDelta;

      // Recent window benchmarks
      const recentClientVerticalPeers = getPeersFromIndex(recentRow, recentPeerIndex, 'client');
      const recentAgencyVerticalPeers = getPeersFromIndex(recentRow, recentPeerIndex, 'agency');
      const recentBackendClientPeers = recentClientVerticalPeers.filter((p) => p.hasBackendIntegration);
      const recentBackendAgencyPeers = recentAgencyVerticalPeers.filter((p) => p.hasBackendIntegration);
      const recentClientBenchmarks = getFunnelBenchmarks(recentBackendClientPeers);
      const recentAgencyBenchmarks = getFunnelBenchmarks(recentBackendAgencyPeers);
      const recentSelectedBenchmarks = hasUsableBenchmarks(recentClientBenchmarks) ? recentClientBenchmarks : recentAgencyBenchmarks;
      const recentDecisionCostMetrics = getDecisionCostMetrics(recentRow, recentSelectedBenchmarks);
      const recentEffectiveCostOfMarketing = recentDecisionCostMetrics.effectiveCostOfMarketing;
      const recentDecisionCostKpi = recentEffectiveCostOfMarketing > 0
        ? { key: 'effectiveCostOfMarketing', label: recentDecisionCostMetrics.decisionCostLabel, better: 'lower', benchmarkScope: 'client_vertical', isEligible: (c) => safeNumber(c.effectiveCostOfMarketing) > 0 }
        : recentPrimaryKpi;
      const enrichedRecentClientPeers = recentClientVerticalPeers.map((p) => ({ ...p, ...getDecisionCostMetrics(p, recentSelectedBenchmarks) }));
      const enrichedRecentAgencyPeers = recentAgencyVerticalPeers.map((p) => ({ ...p, ...getDecisionCostMetrics(p, recentSelectedBenchmarks) }));
      const recentClientDelta = getBenchmarkDelta({ ...recentRow, effectiveCostOfMarketing: recentEffectiveCostOfMarketing }, enrichedRecentClientPeers, recentDecisionCostKpi);
      const recentAgencyDelta = getBenchmarkDelta({ ...recentRow, effectiveCostOfMarketing: recentEffectiveCostOfMarketing }, enrichedRecentAgencyPeers, recentDecisionCostKpi);
      const recentBenchmarkDelta = recentDecisionCostKpi?.benchmarkScope === 'agency_vertical' ? recentAgencyDelta : recentClientDelta;

      const spendShare = getSpendShareFromIndex(row, peerIndex);
      const confidenceScore = getConfidenceScore(row);
      const confidenceLabel = getConfidenceLabel(confidenceScore);
      const trendLabel = getTrendLabel(benchmarkDelta, recentBenchmarkDelta, decisionCostKpi?.better || primaryKpi?.better || 'higher');

      const creativeBenchmarkDelta = getBenchmarkDelta(row, agencyVerticalPeers, { key: 'ctr', label: 'CTR', better: 'higher', benchmarkScope: 'agency_vertical', isEligible: (c) => c.impressions > 0 });
      const businessBenchmarkDelta = primaryKpi?.benchmarkScope === 'agency_vertical' ? agencyVerticalDelta : clientVerticalDelta;

      // Fix 8: Use vertical-aware targets for scoring
      const creativeScore = getBenchmarkAdjustedScore(getCreativeScore(row, verticalTargets.creative), creativeBenchmarkDelta, 'higher');
      const businessScore = getBenchmarkAdjustedScore(
        getBusinessScore({ ...row, effectiveCostOfMarketing }, verticalTargets.business),
        businessBenchmarkDelta,
        decisionCostKpi?.better || primaryKpi?.better || 'higher'
      );
      const overallScore = getOverallScore({ ...row, maturityLabel: row.maturityLabel }, creativeScore, businessScore, confidenceScore);
      const creativeVerdict = getCreativeVerdict({ ...row, maturityLabel: row.maturityLabel }, creativeScore, overallScore);
      const diagnosticLabel = getDiagnosticLabel({ ...row, maturityLabel: row.maturityLabel }, creativeScore, businessScore);

      const benchmarkScope = decisionCostKpi?.benchmarkScope === 'agency_vertical' ? 'agency_vertical' : 'client_vertical';
      const peerRowsForRank = benchmarkScope === 'agency_vertical' ? enrichedAgencyVerticalPeers : enrichedClientVerticalPeers;
      const whyDrivers = getWhyDrivers(enrichedRow, peerRowsForRank);

      const baseRecommendation = getRecommendation(enrichedRow, decisionCostKpi, benchmarkDelta);
      const overallRecommendation = gateRecommendationByComConfidence({
        recommendation: baseRecommendation, primaryKpi: decisionCostKpi,
        comConfidenceBand, dealValueStability, confidenceScore, whyDrivers,
      });
      const whyLabel = getWhyLabel({
        row, primaryKpi: decisionCostKpi || primaryKpi, benchmarkDelta, benchmarkScope,
        recommendation: overallRecommendation, diagnosticLabel, maturityLabel: row.maturityLabel,
        spendShare, confidenceLabel, whyDrivers,
      });
      const whyDriverChips = getTopWhyDriverChips(whyDrivers);

      const recentBaseRecommendation = getRecommendation(
        { ...recentRow, effectiveCostOfMarketing: recentEffectiveCostOfMarketing },
        recentDecisionCostKpi || recentPrimaryKpi, recentBenchmarkDelta
      );
      const recentRecommendation = gateRecommendationByComConfidence({
        recommendation: recentBaseRecommendation, primaryKpi: recentDecisionCostKpi || recentPrimaryKpi,
        comConfidenceBand: recentDecisionCostMetrics.comConfidenceBand,
        dealValueStability: recentDecisionCostMetrics.dealValueStability,
        confidenceScore, whyDrivers,
      });

      const peerRankLabel = getPeerRankLabel(enrichedRow, peerRowsForRank, decisionCostKpi || primaryKpi);
      const stabilityLabel = getStabilityLabel(recentRecommendation, overallRecommendation, trendLabel);
      const directionalDelta = getDirectionalDelta(benchmarkDelta, decisionCostKpi?.better || primaryKpi?.better || 'higher');

      const pausePriority = getPausePriority({ overallRecommendation, recentRecommendation, creativeVerdict, diagnosticLabel, confidenceScore, spendShare, directionalDelta });
      const buildPriority = getBuildPriority({ overallRecommendation, recentRecommendation, creativeVerdict, confidenceScore, spendShare, directionalDelta, stageLabel: row.maturityLabel });

      return {
        ...row,
        stageLabel: row.maturityLabel,
        creativeScore, businessScore, overallScore, creativeVerdict, diagnosticLabel,
        predictedCostOfMarketing,
        predictedCostOfMarketingLabel: predictedCostOfMarketing == null ? '-' : formatPercentLabel(predictedCostOfMarketing),
        effectiveCostOfMarketing,
        effectiveCostOfMarketingLabel: effectiveCostOfMarketing == null ? '-' : formatPercentLabel(effectiveCostOfMarketing),
        comConfidenceBand, comConfidenceLabel, dealValueStability, dealValueStabilityDampener, decisionCostActualWeight,
        confidenceScore, confidenceLabel,
        spendShare, spendShareLabel: formatPercentLabel(spendShare),
        pausePriority, pausePriorityLabel: formatPriorityLabel(pausePriority),
        buildPriority, buildPriorityLabel: formatPriorityLabel(buildPriority),
        peerRankLabel, stabilityLabel,
        primaryKpiLabel: primaryKpi?.label || '-',
        clientVerticalDelta, agencyVerticalDelta,
        clientVerticalDeltaLabel: formatDeltaLabel(clientVerticalDelta),
        agencyVerticalDeltaLabel: formatDeltaLabel(agencyVerticalDelta),
        decisionBenchmarkDelta: benchmarkDelta,
        recentRecommendation, overallRecommendation, trendLabel,
        whyLabel, whyDriverChips,
      };
    });
  }, [filteredRecentRows, filteredRows, viewMode]);

  // ── Derived data ──────────────────────────────────────────────────────

  const totals = React.useMemo(() => {
    const base = decisionRows.reduce((acc, row) => {
      acc.spend += safeNumber(row.spend); acc.leads += safeNumber(row.leads);
      acc.selfBook += safeNumber(row.selfBook); acc.booked += safeNumber(row.booked);
      acc.scheduled += safeNumber(row.scheduled); acc.issues += safeNumber(row.issues);
      acc.demos += safeNumber(row.demos); acc.grossSold += safeNumber(row.grossSold);
      acc.netSold += safeNumber(row.netSold); acc.netAmount += safeNumber(row.netAmount);
      return acc;
    }, { spend: 0, leads: 0, selfBook: 0, booked: 0, scheduled: 0, issues: 0, demos: 0, grossSold: 0, netSold: 0, netAmount: 0 });
    return computeDerivedMetrics(base);
  }, [decisionRows]);

  const adHierarchy = React.useMemo(
    () => (viewMode === 'ad' ? buildAdHierarchy(decisionRows) : []),
    [decisionRows, viewMode]
  );

  React.useEffect(() => {
    if (viewMode !== 'ad') return;
    setExpandedClients((prev) => {
      const next = {};
      adHierarchy.forEach((g) => { next[g.key] = prev[g.key] ?? true; }); // clients expanded
      return Object.keys(next).length === Object.keys(prev).length && Object.keys(next).every((k) => prev[k] === next[k]) ? prev : next;
    });
    setExpandedCampaigns((prev) => {
      const next = {};
      adHierarchy.forEach((g) => g.campaigns.forEach((c) => { next[c.key] = prev[c.key] ?? false; })); // campaigns collapsed
      return Object.keys(next).length === Object.keys(prev).length && Object.keys(next).every((k) => prev[k] === next[k]) ? prev : next;
    });
    setExpandedAdSets((prev) => {
      const next = {};
      adHierarchy.forEach((g) => g.campaigns.forEach((c) => c.adSets.forEach((a) => { next[a.key] = prev[a.key] ?? false; }))); // ad sets collapsed
      return Object.keys(next).length === Object.keys(prev).length && Object.keys(next).every((k) => prev[k] === next[k]) ? prev : next;
    });
  }, [adHierarchy, viewMode]);

  // ── QA ────────────────────────────────────────────────────────────────

  const fetchQaBackendRecords = React.useCallback(async (row) => {
    const response = await api.request({
      url: 'dashboard/backend-data/', method: 'GET',
      payload: { client: row.clientId, ad_id: row.adId, date_field: 'lead_created_date', date_after: formatDateToYMD(startDate), date_before: formatDateToYMD(endDate), ordering: '-lead_created_date', page_size: 100 },
    });
    const data = response?.data;
    const results = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
    return { records: results, totalCount: safeNumber(data?.count ?? results.length) };
  }, [endDate, startDate]);

  const handleToggleQaRow = React.useCallback(async (row) => {
    if (!row?.uuid) return;
    const rowKey = row.uuid;
    if (expandedQaRows[rowKey]) { setExpandedQaRows((p) => ({ ...p, [rowKey]: false })); return; }
    setExpandedQaRows((p) => ({ ...p, [rowKey]: true }));
    if (qaRowDetails[rowKey] || qaRowLoading[rowKey]) return;
    setQaRowLoading((p) => ({ ...p, [rowKey]: true }));
    setQaRowErrors((p) => ({ ...p, [rowKey]: '' }));
    try {
      const detail = await fetchQaBackendRecords(row);
      setQaRowDetails((p) => ({ ...p, [rowKey]: detail }));
    } catch (err) {
      const message = err?.response?.data?.detail || err?.message || 'Unable to load backend QA records.';
      setQaRowErrors((p) => ({ ...p, [rowKey]: message }));
    } finally {
      setQaRowLoading((p) => ({ ...p, [rowKey]: false }));
    }
  }, [expandedQaRows, fetchQaBackendRecords, qaRowDetails, qaRowLoading]);

  React.useEffect(() => {
    setExpandedQaRows({}); setQaRowDetails({}); setQaRowLoading({}); setQaRowErrors({});
  }, [startDate, endDate, viewMode]);

  // Decision board data removed — recommendation summary bar + focus mode in table replace it

  const dimensionColumns = React.useMemo(() => getDimensionColumns(viewMode), [viewMode]);

  // ── Handlers ──────────────────────────────────────────────────────────

  const handleDateRangeChange = (event) => {
    const selection = event?.selection || {};
    if (selection.startDate) setStartDate(selection.startDate);
    if (selection.endDate) setEndDate(selection.endDate);
  };

  const handleOpenPreview = React.useCallback((row) => { setPreviewRow(row); setPauseNote(buildPauseAuditNote(row)); }, []);
  const handleClosePreview = React.useCallback(() => { setPreviewRow(null); setPauseNote(''); setPauseLoading(false); }, []);
  const toggleClientExpanded = React.useCallback((key) => { setExpandedClients((p) => ({ ...p, [key]: !(p[key] ?? true) })); }, []);
  const toggleCampaignExpanded = React.useCallback((key) => { setExpandedCampaigns((p) => ({ ...p, [key]: !(p[key] ?? true) })); }, []);
  const toggleAdSetExpanded = React.useCallback((key) => { setExpandedAdSets((p) => ({ ...p, [key]: !(p[key] ?? true) })); }, []);

  const handlePauseAd = React.useCallback(async () => {
    if (!previewRow?.clientId || !previewRow?.adId || pauseLoading) return;
    setPauseLoading(true); setError(null);
    try {
      await pauseAdSummaryAd({
        client_id: previewRow.clientId, ad_id: previewRow.adId,
        final_note: pauseNote || buildPauseAuditNote(previewRow),
        row_snapshot: previewRow,
      });
      const applyPaused = (currentRows) => currentRows.map((r) => (
        r.clientId !== previewRow.clientId || r.adId !== previewRow.adId ? r : { ...r, isActive: false, activeStatusLabel: 'Paused' }
      ));
      setRows((p) => applyPaused(p));
      setRecentRows((p) => applyPaused(p));
      setPreviewRow((p) => p ? { ...p, isActive: false, activeStatusLabel: 'Paused' } : p);
    } catch (err) {
      const responseData = err?.response?.data;
      const detail = responseData?.detail || responseData?.ad_id || responseData?.client_id || 'Unable to pause the selected Meta ad.';
      setError(Array.isArray(detail) ? detail.join(' ') : String(detail));
    } finally { setPauseLoading(false); }
  }, [pauseLoading, pauseNote, previewRow]);

  // ── Computed labels ───────────────────────────────────────────────────

  const pageSubheader = {
    ad: 'Broad campaigns work best at the ad level. Creative evaluation combines Facebook delivery, lead progression, and business-outcome guidance. Clients without backend integrations stop at lead/self-book stages and are excluded from later-stage benchmark math.',
    theme: 'Theme-level creative pattern rollup for broad creative analysis. Use this to spot reusable patterns across the current scope.',
    subtheme: 'Subtheme-level creative pattern rollup for broad creative analysis. Use this to find more specific winners worth briefing into the next round.',
  }[viewMode];

  const backendCapableRows = decisionRows.filter((r) => r.hasBackendIntegration).length;
  const parsedThemeCoverage = decisionRows.length ? safeDiv(decisionRows.filter((r) => r.themeName).length, decisionRows.length) : 0;
  const parsedSubthemeCoverage = decisionRows.length ? safeDiv(decisionRows.filter((r) => r.subthemeName).length, decisionRows.length) : 0;
  const matureCohortCutoffLabel = React.useMemo(() => { const r = filteredRows.find((row) => row.matureEndDate); return formatShortDate(r?.matureEndDate); }, [filteredRows]);
  const currentRangeDays = getDateRangeDays(startDate, endDate);
  const recommendedRangeGuidance = React.useMemo(
    () => getRecommendedLookbackGuidance({ rows: filteredRows, recentRows: filteredRecentRows, currentRangeDays }),
    [currentRangeDays, filteredRecentRows, filteredRows]
  );

  // ─── JSX ──────────────────────────────────────────────────────────────

  return (
    <Page title="Creative Performance" fullWidth>
      <Stack spacing={3}>
        {error && <Alert severity="error">{error}</Alert>}

        {/* ── Compact controls ──────────────────────────────────────────── */}
        <Card>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Stack spacing={1.5}>
              {/* Row 1: Date picker + Parent/Client filters + summary stats */}
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', lg: 'center' }}>
                <Box sx={{ minWidth: 280, maxWidth: { lg: 320 } }}>
                  <DateRangePicker startDate={startDate} endDate={endDate} onChange={handleDateRangeChange} style={{ width: '100%' }} />
                </Box>
                <Autocomplete multiple size="small" sx={{ minWidth: 200, flex: 1 }} options={parentCompanyOptions.map((o) => ({ label: o.name, value: o.id }))} value={parentCompanyOptions.filter((o) => selectedParentCompanies.includes(o.id)).map((o) => ({ label: o.name, value: o.id }))} onChange={(_, v) => setSelectedParentCompanies(v.map((o) => o.value))} isOptionEqualToValue={(o, v) => o.value === v.value} getOptionLabel={(o) => o.label || ''} renderInput={(params) => <TextField {...params} label="Parent Companies" />} />
                <Autocomplete multiple size="small" sx={{ minWidth: 200, flex: 1 }} options={clientOptions.map((o) => ({ label: o.name, value: o.id }))} value={clientOptions.filter((o) => selectedClients.includes(o.id)).map((o) => ({ label: o.name, value: o.id }))} onChange={(_, v) => setSelectedClients(v.map((o) => o.value))} isOptionEqualToValue={(o, v) => o.value === v.value} getOptionLabel={(o) => o.label || ''} renderInput={(params) => <TextField {...params} label="Clients" />} />
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
                  <Box sx={{ textAlign: 'center', px: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Spend</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}><DollarFormatter value={totals.spend} /></Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', px: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Backend</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{backendCapableRows}/{decisionRows.length}</Typography>
                  </Box>
                  {viewMode === 'ad' && recommendedRangeGuidance ? (
                    <Tooltip title={recommendedRangeGuidance.reason} arrow>
                      <Box sx={{ textAlign: 'center', px: 1.5, cursor: 'help' }}>
                        <Typography variant="caption" color="text.secondary">Rec. Range</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'info.main' }}>{recommendedRangeGuidance.recommendedDays}d</Typography>
                      </Box>
                    </Tooltip>
                  ) : null}
                </Stack>
              </Stack>

              {/* Row 2: All toggles on one line */}
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <ToggleButtonGroup value={viewMode} exclusive size="small" onChange={(_, v) => { if (v) setViewMode(v); }} sx={{ '& .MuiToggleButton-root': { border: 0, px: 1.4, py: 0.5, borderRadius: 2, fontWeight: 700, fontSize: 13 } }}>
                  {VIEW_MODE_OPTIONS.map((o) => <ToggleButton key={o.key} value={o.key}>{o.label}</ToggleButton>)}
                </ToggleButtonGroup>
                {viewMode === 'ad' ? (
                  <ToggleButtonGroup value={showPausedAds ? 'all' : 'active'} exclusive size="small" onChange={(_, v) => { if (v) setShowPausedAds(v === 'all'); }} sx={{ '& .MuiToggleButton-root': { border: 0, px: 1.4, py: 0.5, borderRadius: 2, fontWeight: 700, fontSize: 13 } }}>
                    <ToggleButton value="active">Active Only</ToggleButton>
                    <ToggleButton value="all">Include Paused</ToggleButton>
                  </ToggleButtonGroup>
                ) : null}
                <ToggleButtonGroup value={backendCoverageFilter} exclusive size="small" onChange={(_, v) => { if (v) setBackendCoverageFilter(v); }} sx={{ '& .MuiToggleButton-root': { border: 0, px: 1.4, py: 0.5, borderRadius: 2, fontWeight: 700, fontSize: 13 } }}>
                  <ToggleButton value="all">All Clients</ToggleButton>
                  <ToggleButton value="backend_only">Backend Only</ToggleButton>
                </ToggleButtonGroup>
                {matureCohortCutoffLabel ? <Typography variant="caption" color="text.secondary">Mature cohorts through {matureCohortCutoffLabel}</Typography> : null}
                <Box sx={{ flex: 1 }} />
                <Button size="small" variant="text" onClick={() => setShowFilters((p) => !p)} sx={{ fontWeight: 700, fontSize: 12 }}>
                  {showFilters ? 'Hide Filters' : 'More Filters'}
                </Button>
              </Stack>

              {/* Row 3: Collapsible filters */}
              {showFilters ? (
                <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)', xl: 'repeat(5, 1fr)' } }}>
                  <Autocomplete multiple size="small" options={filterOptions.verticals} value={filterOptions.verticals.filter((o) => selectedVerticals.includes(o.value))} onChange={(_, v) => setSelectedVerticals(v.map((o) => o.value))} isOptionEqualToValue={(o, v) => o.value === v.value} getOptionLabel={(o) => o.label || ''} renderInput={(params) => <TextField {...params} label="Vertical" />} />
                  <Autocomplete multiple size="small" options={filterOptions.themes} value={filterOptions.themes.filter((o) => selectedThemes.includes(o.value))} onChange={(_, v) => setSelectedThemes(v.map((o) => o.value))} isOptionEqualToValue={(o, v) => o.value === v.value} getOptionLabel={(o) => o.label || ''} renderInput={(params) => <TextField {...params} label="Theme" />} />
                  <Autocomplete multiple size="small" options={filterOptions.subthemes} value={filterOptions.subthemes.filter((o) => selectedSubthemes.includes(o.value))} onChange={(_, v) => setSelectedSubthemes(v.map((o) => o.value))} isOptionEqualToValue={(o, v) => o.value === v.value} getOptionLabel={(o) => o.label || ''} renderInput={(params) => <TextField {...params} label="Subtheme" />} />
                  <Autocomplete multiple size="small" options={filterOptions.adTypes} value={filterOptions.adTypes.filter((o) => selectedAdTypes.includes(o.value))} onChange={(_, v) => setSelectedAdTypes(v.map((o) => o.value))} isOptionEqualToValue={(o, v) => o.value === v.value} getOptionLabel={(o) => o.label || ''} renderInput={(params) => <TextField {...params} label="Ad Type" />} />
                  <Autocomplete multiple size="small" options={filterOptions.notes} value={filterOptions.notes.filter((o) => selectedNotes.includes(o.value))} onChange={(_, v) => setSelectedNotes(v.map((o) => o.value))} isOptionEqualToValue={(o, v) => o.value === v.value} getOptionLabel={(o) => o.label || ''} renderInput={(params) => <TextField {...params} label="Notes" />} />
                </Box>
              ) : null}
            </Stack>
          </CardContent>
        </Card>

        {/* ── Main table ───────────────────────────────────────────────── */}
        <Card>
          <CardContent sx={{ p: 0 }}>
            <CreativeTable
              viewMode={viewMode}
              decisionRows={decisionRows}
              adHierarchy={adHierarchy}
              dimensionColumns={dimensionColumns}
              totals={totals}
              loading={loading}
              loadingClientMeta={loadingClientMeta}
              startDate={startDate}
              endDate={endDate}
              onPreviewOpen={handleOpenPreview}
              onToggleQa={handleToggleQaRow}
              expandedClients={expandedClients}
              expandedCampaigns={expandedCampaigns}
              expandedAdSets={expandedAdSets}
              expandedQaRows={expandedQaRows}
              qaRowDetails={qaRowDetails}
              qaRowLoading={qaRowLoading}
              qaRowErrors={qaRowErrors}
              toggleClientExpanded={toggleClientExpanded}
              toggleCampaignExpanded={toggleCampaignExpanded}
              toggleAdSetExpanded={toggleAdSetExpanded}
            />
          </CardContent>
        </Card>

        {/* ── Preview / Pause dialog ───────────────────────────────────── */}
        <AdPreviewDialog
          previewRow={previewRow}
          pauseNote={pauseNote}
          pauseLoading={pauseLoading}
          onPauseNoteChange={setPauseNote}
          onPause={handlePauseAd}
          onClose={handleClosePreview}
        />
      </Stack>
    </Page>
  );
}
